import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetProjectByIdQuery, useUnassignProjectKnowledgeBaseMutation } from 'src/services';
import { ProductsType, ProductsTypeBack } from 'src/constants/products';
import { useGetKnowledgeBaseQuery } from 'src/services/knowledgeBaseService';
import { clsx } from 'clsx';
import { useTheme } from 'src/provider/ThemeProvider';
import { useNotification } from 'src/hooks/useNotification';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useAppSelector } from 'src/store';
import { Button } from 'src/components/ui/button';
import { SettingsPopoverButton } from 'src/components/SettingsPopoverButton/SettingsPopoverButton';
import { Separator } from 'src/components/ui/separator';
import { CrossCircleIcon } from 'src/assets/icons/crossCircleIcon';
import { GoToIcon } from 'src/assets/icons/goToIcon';
import { DeletePopup } from 'src/components/DeletePopup/DeletePopup';
import { AssignKnowledgeBasePopup } from 'src/pages/ControlDeskPage/DataSourceSection/KnowledgeBases/AssignKnowledgeBasePopup/AssignKnowledgeBasePopup';

export const ChangeKnowledgeBase = () => {
  const [knowledgeBases, setKnowledgeBases] = useState(null);
  const { id } = useParams();
  const { config } = useTheme();
  const navigate = useNavigate();
  const notification = useNotification();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const { data: currentProject, isFetching: isProjectFetching } = useGetProjectByIdQuery({
    id: id,
  }) as any;

  const { data: knowledgeBasesData, isFetching: isFetchingKnowledgeBaseDataByProjectId } =
    useGetKnowledgeBaseQuery(
      {
        load_projects: true,
        page_size: 100,
      },
      { refetchOnMountOrArgChange: true },
    ) as any;

  const [unassignKB, { isLoading: unassignKBIsLoading, isSuccess: unassignKBisSuccess }] =
    useUnassignProjectKnowledgeBaseMutation();

  useEffect(() => {
    if (unassignKBisSuccess) {
      notification.success('Knowledge Base has been unassigned.');
    }
  }, [unassignKBisSuccess, unassignKBIsLoading]);

  useEffect(() => {
    if (knowledgeBasesData) {
      const projectKnowledgeBases = knowledgeBasesData.items.filter((kb) => {
        return kb.projects.some((project) => `${project.id}` === id);
      });

      setKnowledgeBases(projectKnowledgeBases);
    }
  }, [knowledgeBasesData, id]);

  useEffect(() => {
    if (knowledgeBasesData) {
      if (isCustomer && !knowledgeBasesData?.items?.length) {
        navigate(`${PATH_DASHBOARD.PROJECTS}/${id}/new/2?type=${projectTypeVar}`);
      }
    }
  }, [knowledgeBases]);

  const handleUnassignKb = (id) => {
    unassignKB({
      project_type: ProductsTypeBack[currentProject?.project_type],
      projectId: currentProject.id,
      body: [id],
    });
  };

  const isCustomer = currentProject?.project_type === ProductsType.customer;
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);

  return (
    <div className="flex flex-col items-start gap-2.5 w-full">
      {knowledgeBases?.length ? (
        <>
          {knowledgeBases?.map((item) => {
            return (
              <div
                className={clsx(
                  (isProjectFetching || isFetchingKnowledgeBaseDataByProjectId) &&
                    'blur-sm animate-pulse pointer-events-none',
                  'w-full pb-2.5 border-b border-b-light flex justify-between items-center',
                )}
                key={item.id}
              >
                <p title={item.name} className="text-sm font-medium text-ebony truncate opacity-80">
                  {item.name}
                </p>
                <>
                  {' '}
                  <SettingsPopoverButton
                    content={
                      <>
                        <div className="flex flex-col items-center sm:justify-end justify-between">
                          <div className="flex flex-col w-full">
                            <Link
                              onClick={(e) => {
                                if (!currentProject?.is_active || isInactivePlan) {
                                  e.preventDefault();
                                }
                              }}
                              to={`${PATH_DASHBOARD.AI_KNOWLEDGE_BASE}/${item.id}`}
                            >
                              <Button
                                disabled={!currentProject?.is_active || isInactivePlan}
                                variant="ghost"
                                className="w-full justify-start !rounded-none"
                              >
                                <div className="flex items-center gap-2.5">
                                  <GoToIcon />
                                  <p className="opacity-80 font-medium text-sm">
                                    Go to Knowledge Base
                                  </p>
                                </div>
                              </Button>
                            </Link>
                            <Separator className={'bg-[#E3E6E8]'} />
                          </div>
                          <div className="flex flex-col w-full">
                            <DeletePopup
                              deleteHandler={() => handleUnassignKb(item.id)}
                              title="Deactivate?"
                              description="Are you sure you want to deactivate?"
                              deleteBtnText="Yes, deactivate"
                              triggerIcon={
                                <Button
                                  disabled={
                                    !currentProject?.is_active ||
                                    isInactivePlan ||
                                    unassignKBIsLoading ||
                                    isFetchingKnowledgeBaseDataByProjectId
                                  }
                                  variant="ghost"
                                  className={clsx(
                                    (unassignKBIsLoading ||
                                      isFetchingKnowledgeBaseDataByProjectId) &&
                                      'animate-pulse',
                                    'w-full justify-start !rounded-none',
                                  )}
                                >
                                  <div className="flex items-center gap-2.5">
                                    <CrossCircleIcon />
                                    <p className="opacity-80 font-medium text-sm">Deactivate</p>
                                  </div>
                                </Button>
                              }
                            />
                          </div>
                        </div>
                      </>
                    }
                  />
                </>
              </div>
            );
          })}
        </>
      ) : (
        <p
          className={clsx(
            isFetchingKnowledgeBaseDataByProjectId && 'blur-sm animate-pulse pointer-events-none',
            'opacity-30 font-jamjuree font-medium text-sm truncate',
          )}
        >
          AI is not connected to any knowledge base
        </p>
      )}
      <AssignKnowledgeBasePopup
        knowledgeBases={knowledgeBasesData?.items}
        disabled={isInactivePlan}
        trigger={
          <Button variant="ghost" disabled={!currentProject?.is_active || isInactivePlan}>
            <p className={clsx(config?.textColorClassName, 'font-medium text-base-xs')}>
              Assign knowledge base +
            </p>
          </Button>
        }
      />
    </div>
  );
};
